import React, { useState, useRef, useEffect } from 'react';
import { ChevronDown, Plus, ClipboardCheck, FileSignature, FileImage, Receipt, Search, Layers } from 'lucide-react';

/**
 * Split-button used everywhere we want to "create an appraisal".
 *
 * Batch T Phase 4 (2026-02 · user Trevor) — consolidated appraisal
 * quote_types to 3 canonical refs matching the report_variant taxonomy
 * used across Report Setup / Estimate Templates / Overwatch:
 *
 *   · Instant Free         (was: Free Appraisal Report / Free Estimate)
 *   · Detailed 1-Comment   (was: Single Defect Report / Appraisal Inspection)
 *   · Detailed Multi       (was: Multi Defect Report and Photos / Appraisal Report)
 *
 * The 4th kind (`quote_only`) still uses its own "Quote" ref — it's a
 * direct-quote flow, not an appraisal.
 *
 * Labels + keys must match verbatim across:
 *   · NewAppraisalKindButton (this file)
 *   · db.quote_types.ref     (seeded via seed_appraisal_canonical.py)
 *   · overwatch_templates.py Action 1 radio_choice options
 *   · WorkflowRunner display_message copy library
 *
 * Featured kinds (top of menu):
 *   • Instant Free            → desktop-only estimate, no on-site visit
 *   • Detailed 1-Comment      → focused on-site inspection of one issue
 *   • Detailed Multi          → full on-site inspection with photos
 *   • Quote                   → direct quote (no appraisal report); status=Quoted
 *
 * Other quote types (from `/quote-types?active_only=true`) use
 * status='Appraisal Required' by default. Legacy soft-deleted appraisal
 * refs (Free Estimate, Appraisal Estimate, Appraisal Quote, Appraisal
 * Inspection, Inspection Appraisal, Appraisal Report) are filtered out
 * by the `active_only` flag so operators never see them in the picker.
 *
 * Props:
 *   onSelect: (kindKey, presets) => void
 *     - kindKey: 'free_estimate' | 'single_defect' | 'multi_defect' | 'quote_only' | quote_type ref string
 *     - presets: { quote_type_ref, appraisal_status }
 *   variant: 'orange' | 'emerald'
 *   testIdPrefix: prefix for data-testids (default 'new-appraisal')
 *   compact: boolean (smaller variant for property/client cards)
 *   api: axios instance used to fetch /quote-types (optional — if omitted, only featured kinds show)
 */

export const APPRAISAL_KINDS = {
  free_estimate: {
    label: 'Instant Free',
    actionLabel: 'New Instant Free Appraisal',
    quote_type_ref: 'Instant Free',
    appraisal_status: 'Client Data',
    icon: ClipboardCheck,
    description: 'Desktop-only appraisal — Google Earth + roof template, no on-site visit.',
    accent: '#D35400',
  },
  single_defect: {
    label: 'Detailed 1-Comment',
    actionLabel: 'New Detailed 1-Comment Appraisal',
    quote_type_ref: 'Detailed 1-Comment',
    appraisal_status: 'Client Data',
    icon: FileSignature,
    description: 'On-site appraisal focused on one specific defect and its remedy.',
    accent: '#316536',
  },
  multi_defect: {
    label: 'Detailed Multi',
    actionLabel: 'New Detailed Multi Appraisal',
    quote_type_ref: 'Detailed Multi',
    appraisal_status: 'Client Data',
    icon: FileImage,
    description: 'Full on-site inspection — every defect recorded with photos and a final quotation.',
    accent: '#065F46',
  },
  quote_only: {
    label: 'Quote',
    actionLabel: 'New Quote',
    quote_type_ref: 'Quote',
    // Direct quote requests skip the appraisal workflow — they go straight
    // to "Quoted" so the user can immediately add quote items and send the
    // quotation. No on-site appraisal report is generated.
    appraisal_status: 'Quote Sent',
    icon: Receipt,
    description: 'Direct quote request — no appraisal report required. Skips straight to quoting.',
    accent: '#1E40AF',
  },
};

// The 3 featured quote_type_ref strings — used to dedupe them from the
// "All quotation types" list so they don't appear twice.
const FEATURED_REFS = new Set(Object.values(APPRAISAL_KINDS).map(k => k.quote_type_ref));

export default function NewAppraisalKindButton({ onSelect, variant = 'orange', testIdPrefix = 'new-appraisal', compact = false, api = null, roofTypes = null, preferredQuoteType = '', preferredQuoteTypes = null }) {
  const [open, setOpen] = useState(false);
  const [quoteTypes, setQuoteTypes] = useState([]);
  const [filter, setFilter] = useState('');
  const wrapRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => { if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);

  // Iter181 — lazy-load the full quote_types list the first time the
  // menu opens so the dropdown gives access to all 34 types (e.g.
  // "Appraisal", "LongRun Iron Reroof", "Remedial Repairs", etc.).
  useEffect(() => {
    if (!open || !api || quoteTypes.length) return;
    (async () => {
      try {
        const { data } = await api.get('/quote-types?active_only=true');
        setQuoteTypes(Array.isArray(data) ? data : []);
      } catch { setQuoteTypes([]); }
    })();
  }, [open, api, quoteTypes.length]);

  const pickKind = (kindKey) => {
    setOpen(false);
    setFilter('');
    const k = APPRAISAL_KINDS[kindKey];
    onSelect?.(kindKey, { quote_type_ref: k.quote_type_ref, appraisal_status: k.appraisal_status });
  };

  const pickRawQuoteType = (qt) => {
    setOpen(false);
    setFilter('');
    // For non-featured quote types we use a generic preset. The default
    // status is "Appraisal Required" except for the "Appraisal Quote"
    // category which historically went straight to "Quoted".
    const isQuoteCategory = (qt.quote_categories || qt.quote_category || []).some?.(
      c => c === 'Repairs' || c === 'Reroof' || c === 'Restoration' || c === 'Painting' || c === 'Guttering'
    );
    onSelect?.(qt.ref, {
      quote_type_ref: qt.ref,
      appraisal_status: isQuoteCategory ? 'Quote Sent' : 'Client Data',
    });
  };

  // Iter207 — Recommended-for-roof cascade. When `roofTypes` is
  // supplied (e.g. from the Property page's Roof Details), filter the
  // quote_types whose `roof_types` array overlaps with the property's
  // actual roofs. Normalisation handles the mismatch between how the
  // property records roof types (e.g. "Mod Conc", "Clay Tile") and how
  // quote_types tag them ("Concrete", "Tin Tile", "Decramastic", "Iron").
  const propertyRoofs = Array.isArray(roofTypes)
    ? Array.from(new Set(roofTypes.map(r => (r || '').trim()).filter(Boolean)))
    : [];
  // Family map — left side = property roof keyword, right side = quote_type roof_types family
  const ROOF_FAMILY = {
    iron:        ['Iron'],
    longrun:     ['Iron'],
    'long run':  ['Iron'],
    lri:         ['Iron'],
    concrete:    ['Concrete', 'Modern Concrete', 'Old Concrete'],
    'modern concrete': ['Modern Concrete'],
    'old concrete':    ['Old Concrete'],
    'mod conc':  ['Concrete', 'Modern Concrete'],
    clay:        ['Clay'],
    butynol:     ['Butynol'],
    polycarbonate: ['Polycarbonate'],
    tile:        ['Concrete', 'Tin Tile', 'Decramastic'],
    'clay tile': ['Concrete'],   // clay tile shares Concrete-tile workflows
    decramastic: ['Decramastic', 'Tin Tile'],
    decra:       ['Decramastic', 'Tin Tile'],
    'tin tile':  ['Tin Tile', 'Decramastic'],
    tin:         ['Tin Tile'],
  };
  const propertyFamilies = new Set();
  propertyRoofs.forEach(r => {
    const rl = r.toLowerCase();
    Object.entries(ROOF_FAMILY).forEach(([kw, fams]) => {
      if (rl.includes(kw)) fams.forEach(f => propertyFamilies.add(f));
    });
  });
  const matchesPropertyRoof = (qt) => {
    if (propertyFamilies.size === 0) return false;
    const qrt = (qt.roof_types || []).map(s => String(s).trim());
    if (qrt.length === 0 || qrt.includes('All')) return true; // applies-to-all
    return qrt.some(f => propertyFamilies.has(f));
  };
  const recommendedForRoof = quoteTypes
    .filter(q => !FEATURED_REFS.has(q.ref))
    .filter(matchesPropertyRoof)
    .filter(q => !filter || q.ref.toLowerCase().includes(filter.toLowerCase()));

  // Iter208 — when the property has `preferred_quote_type` (single) or
  // `preferred_quote_types` (multi — iter303ae), sort matching quote_types
  // to the top with a ★ star. Multi wins if provided; falls back to single.
  const preferredList = Array.isArray(preferredQuoteTypes) && preferredQuoteTypes.length
    ? preferredQuoteTypes.map(v => String(v).toLowerCase().trim()).filter(Boolean)
    : (preferredQuoteType ? [String(preferredQuoteType).toLowerCase().trim()] : []);
  const isPreferred = (qt) => {
    if (!preferredList.length) return false;
    const cats = (qt.quote_categories || []).map(c => String(c).toLowerCase());
    return preferredList.some(p => cats.includes(p));
  };
  if (preferredList.length) {
    recommendedForRoof.sort((a, b) => {
      const aP = isPreferred(a) ? 0 : 1;
      const bP = isPreferred(b) ? 0 : 1;
      if (aP !== bP) return aP - bP;
      return a.ref.localeCompare(b.ref);
    });
  }

  const baseColour = variant === 'emerald' ? 'bg-emerald-600 hover:bg-emerald-700' : 'bg-[#D35400] hover:bg-[#A04000]';
  const sizeCls = compact ? 'px-2.5 py-1.5 text-xs' : 'px-3.5 py-2 text-sm';
  const sizeCompactCls = compact ? 'px-2 py-1.5' : 'px-2.5 py-2';

  // Group the non-featured quote types by category for clearer scanning.
  const filtered = quoteTypes
    .filter(q => !FEATURED_REFS.has(q.ref))
    .filter(q => !filter || q.ref.toLowerCase().includes(filter.toLowerCase()));
  const groups = {};
  filtered.forEach(q => {
    const cats = q.quote_categories || q.quote_category || ['Other'];
    const cat = Array.isArray(cats) ? (cats[0] || 'Other') : (cats || 'Other');
    if (!groups[cat]) groups[cat] = [];
    groups[cat].push(q);
  });
  const groupOrder = ['Appraisal', 'Repairs', 'Reroof', 'Restoration', 'Maintenance', 'Painting', 'Guttering', 'Treatment', 'Other'];
  const orderedGroups = groupOrder.filter(g => groups[g]).concat(Object.keys(groups).filter(g => !groupOrder.includes(g)));

  return (
    <div ref={wrapRef} className="relative inline-flex">
      <button
        type="button"
        data-testid={`${testIdPrefix}-primary`}
        onClick={() => pickKind('single_defect')}
        className={`flex items-center gap-1.5 ${sizeCls} ${baseColour} text-white rounded-l-md font-semibold transition-all shadow-sm hover:shadow-md`}
      >
        <Plus size={compact ? 12 : 14} strokeWidth={2.5} /> New Appraisal
      </button>
      <button
        type="button"
        data-testid={`${testIdPrefix}-kind-toggle`}
        onClick={() => setOpen(v => !v)}
        aria-haspopup="menu"
        aria-expanded={open}
        className={`flex items-center justify-center ${sizeCompactCls} ${baseColour} text-white rounded-r-md border-l border-white/20 transition-all shadow-sm hover:shadow-md`}
      >
        <ChevronDown size={compact ? 12 : 14} strokeWidth={2.5} className={`transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>

      {open && (
        <div data-testid={`${testIdPrefix}-kind-menu`} role="menu" className="absolute top-full right-0 mt-1.5 w-80 max-h-[80vh] bg-white border border-[#E2E2DF] rounded-lg shadow-xl z-50 overflow-hidden flex flex-col">
          {/* Iter316 · Path 1 · Split by clear section headers so the
              operator sees which items are appraisal REPORTS (top 3)
              and which are actual QUOTES (roof-filtered + all types).
              No component split — just clarity via labelling. */}
          {/* Section 1 — Appraisal Reports (the 3 defect-report kinds) */}
          <div>
            <p className="px-3 pt-3 pb-1 text-[10px] font-bold uppercase tracking-[0.18em] text-emerald-700 flex items-center gap-1.5">
              <ClipboardCheck size={11} /> Appraisal Reports
            </p>
            {Object.entries(APPRAISAL_KINDS)
              .filter(([key]) => key !== 'quote_only') // quote_only lives under Quotes ↓
              .map(([key, k]) => {
                const Icon = k.icon;
                return (
                  <button
                    key={key}
                    role="menuitem"
                    type="button"
                    data-testid={`${testIdPrefix}-kind-${key}`}
                    onClick={() => pickKind(key)}
                    className="w-full px-3 py-2.5 text-left hover:bg-slate-50 transition-colors flex items-start gap-2.5 border-t border-slate-50 first:border-t-0"
                  >
                    <div className="mt-0.5 p-1.5 rounded-md" style={{ backgroundColor: `${k.accent}15` }}>
                      <Icon size={14} style={{ color: k.accent }} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-bold text-slate-900">{k.actionLabel}</p>
                      <p className="text-[11px] text-slate-500 mt-0.5 leading-snug">{k.description}</p>
                    </div>
                  </button>
                );
              })}
          </div>

          {/* Section 2 — Quotes divider header (the direct-Quote kind
              lands here, then the roof-filtered recommendations below,
              then the searchable full 42-row list). */}
          <div className="border-t-2 border-[#D35400]/20 mt-1">
            <p className="px-3 pt-3 pb-1 text-[10px] font-bold uppercase tracking-[0.18em] text-[#D35400] flex items-center gap-1.5">
              <Receipt size={11} /> Quotes
            </p>
            {(() => {
              const quoteKind = APPRAISAL_KINDS.quote_only;
              if (!quoteKind) return null;
              const Icon = quoteKind.icon;
              return (
                <button
                  role="menuitem"
                  type="button"
                  data-testid={`${testIdPrefix}-kind-quote_only`}
                  onClick={() => pickKind('quote_only')}
                  className="w-full px-3 py-2.5 text-left hover:bg-slate-50 transition-colors flex items-start gap-2.5"
                >
                  <div className="mt-0.5 p-1.5 rounded-md" style={{ backgroundColor: `${quoteKind.accent}15` }}>
                    <Icon size={14} style={{ color: quoteKind.accent }} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-bold text-slate-900">{quoteKind.actionLabel}</p>
                    <p className="text-[11px] text-slate-500 mt-0.5 leading-snug">{quoteKind.description}</p>
                  </div>
                </button>
              );
            })()}
          </div>

          {/* Iter207 — Recommended for this property's roof(s).
              Cascade-shortens the picker based on the roof_type
              already recorded against the property. Only renders when
              `roofTypes` was passed in AND there's at least one match
              from the loaded quote_types. */}
          {propertyRoofs.length > 0 && api && (
            <div data-testid={`${testIdPrefix}-recommended-section`}>
              <div className="px-3 pt-2 pb-1 flex items-center justify-between">
                <p className="text-[10px] font-semibold uppercase tracking-[0.15em] text-emerald-700 flex items-center gap-1.5">
                  ↳ Recommended for this roof
                </p>
                <span className="text-[10px] text-slate-400 truncate max-w-[120px]" title={propertyRoofs.join(', ')}>{propertyRoofs.join(', ')}</span>
              </div>
              <div>
                {recommendedForRoof.length === 0 ? (
                  <p className="px-3 py-2 text-[11px] text-slate-400 italic">
                    {quoteTypes.length === 0 ? 'Loading…' : 'No specific quote types match — use the full list below.'}
                  </p>
                ) : (
                  recommendedForRoof.slice(0, 8).map(qt => (
                    <button
                      key={`reco-${qt.ref}`}
                      role="menuitem"
                      type="button"
                      data-testid={`${testIdPrefix}-reco-${qt.ref.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}
                      onClick={() => pickRawQuoteType(qt)}
                      className={`w-full px-3 py-1.5 text-left text-[12px] hover:bg-emerald-50 transition-colors flex items-center justify-between gap-2 border-l-2 ${
                        isPreferred(qt) ? 'border-amber-500 text-amber-900 bg-amber-50/30' : 'border-emerald-400 text-emerald-900'
                      }`}
                    >
                      <span className="font-semibold truncate flex items-center gap-1">
                        {isPreferred(qt) && <span title="Owner's preferred quote type">★</span>}
                        {qt.ref}
                      </span>
                      {(qt.roof_types || []).length > 0 && !qt.roof_types.includes('All') && (
                        <span className="text-[9px] text-emerald-600 flex-shrink-0">{(qt.roof_types || []).slice(0, 2).join('/')}</span>
                      )}
                    </button>
                  ))
                )}
                {recommendedForRoof.length > 8 && (
                  <p className="px-3 py-1 text-[10px] text-slate-400 italic">+ {recommendedForRoof.length - 8} more in full list below</p>
                )}
              </div>
            </div>
          )}

          {/* All quote types — searchable + grouped (sits under the
              Quotes divider — no new section header needed). */}
          {api && (
            <div className="flex flex-col min-h-0">
              <div className="px-3 pt-2 pb-2 flex items-center justify-between flex-shrink-0">
                <p className="text-[10px] font-semibold uppercase tracking-[0.15em] text-slate-500 flex items-center gap-1.5"><Layers size={11} /> ↳ All quotation types</p>
                <span className="text-[10px] text-slate-400">{filtered.length} avail.</span>
              </div>
              <div className="px-3 pb-2 flex-shrink-0">
                <div className="relative">
                  <Search size={11} className="absolute left-2 top-1/2 -translate-y-1/2 text-slate-400" />
                  <input
                    data-testid={`${testIdPrefix}-kind-search`}
                    type="text"
                    value={filter}
                    onChange={e => setFilter(e.target.value)}
                    placeholder="Search e.g. Appraisal, Reroof, Treatment…"
                    className="w-full pl-7 pr-2 py-1.5 text-[11px] border border-[#E2E2DF] rounded focus:outline-none focus:border-[#D35400]"
                  />
                </div>
              </div>
              <div className="overflow-y-auto flex-1">
                {quoteTypes.length === 0 ? (
                  <p className="px-3 py-3 text-[11px] text-slate-400 text-center">Loading types…</p>
                ) : filtered.length === 0 ? (
                  <p className="px-3 py-3 text-[11px] text-slate-400 text-center">No matching types</p>
                ) : (
                  orderedGroups.map(grp => (
                    <div key={grp}>
                      <p className="px-3 pt-1.5 pb-0.5 text-[9px] font-bold uppercase tracking-[0.15em] text-slate-400 bg-slate-50/60">{grp}</p>
                      {groups[grp].map(qt => (
                        <button
                          key={qt.ref}
                          role="menuitem"
                          type="button"
                          data-testid={`${testIdPrefix}-qt-${qt.ref.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}
                          onClick={() => pickRawQuoteType(qt)}
                          className="w-full px-3 py-1.5 text-left text-[12px] text-slate-700 hover:bg-orange-50/40 hover:text-[#A04000] transition-colors flex items-center justify-between gap-2"
                        >
                          <span className="font-medium truncate">{qt.ref}</span>
                          {(qt.roof_types || []).length > 0 && (qt.roof_types || []).join(',') !== 'All' && (
                            <span className="text-[9px] text-slate-400 flex-shrink-0">{(qt.roof_types || []).slice(0, 2).join('/')}</span>
                          )}
                        </button>
                      ))}
                    </div>
                  ))
                )}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
